import { useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllRead, clearAll } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  });

  const toggleSidebar = useCallback(() => {
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      sidebar.classList.toggle('collapsed');
    }
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const ICON_MAP = {
    analysis: 'activity',
    download: 'download',
    profile:  'user',
    system:   'alert-circle',
  };
  const COLOR_MAP = {
    analysis: 'text-primary',
    download: 'text-success',
    profile:  'text-info',
    system:   'text-danger',
  };

  const timeAgo = (iso) => {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60)   return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const displayName = user ? `${user.first_name} ${user.last_name}` : 'User';
  const picUrl = user?.profile_pic_url || (process.env.PUBLIC_URL + '/img/cancepro-logo.png');

  return (
    <nav className="navbar navbar-expand navbar-light navbar-bg">
      <a className="sidebar-toggle js-sidebar-toggle" onClick={toggleSidebar}>
        <i className="hamburger align-self-center"></i>
      </a>

      <div className="navbar-collapse collapse">
        <ul className="navbar-nav navbar-align">
          {/* Notifications */}
          <li className="nav-item dropdown">
            <a
              className="nav-icon dropdown-toggle"
              href="#"
              id="alertsDropdown"
              data-bs-toggle="dropdown"
            >
              <div className="position-relative">
                <i className="align-middle" data-feather="bell"></i>
                {unreadCount > 0 && <span className="indicator">{unreadCount > 9 ? '9+' : unreadCount}</span>}
              </div>
            </a>
            <div
              className="dropdown-menu dropdown-menu-lg dropdown-menu-end py-0"
              aria-labelledby="alertsDropdown"
            >
              <div className="dropdown-menu-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{unreadCount > 0 ? `${unreadCount} New Notification${unreadCount > 1 ? 's' : ''}` : 'Notifications'}</span>
                {notifications.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, fontSize: '0.72rem' }}>
                    {unreadCount > 0 && (
                      <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); markAllRead(); }} style={{ color: 'var(--accent-blue)' }}>Mark all read</a>
                    )}
                    <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearAll(); }} style={{ color: 'var(--text-muted)' }}>Clear</a>
                  </div>
                )}
              </div>
              <div className="list-group" style={{ maxHeight: 340, overflowY: 'auto' }}>
                {notifications.length === 0 ? (
                  <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    No notifications yet
                  </div>
                ) : (
                  notifications.slice(0, 20).map((n) => (
                    <a
                      href="#"
                      key={n.id}
                      className="list-group-item"
                      style={{ background: n.read ? 'transparent' : 'rgba(59,130,246,0.04)' }}
                      onClick={(e) => { e.preventDefault(); if (!n.read) markAsRead(n.id); }}
                    >
                      <div className="row g-0 align-items-center">
                        <div className="col-2">
                          <i className={COLOR_MAP[n.type] || 'text-danger'} data-feather={ICON_MAP[n.type] || 'alert-circle'}></i>
                        </div>
                        <div className="col-10">
                          <div className="text-dark" style={{ fontWeight: n.read ? 400 : 600 }}>{n.title}</div>
                          <div className="text-muted small mt-1">{n.message}</div>
                          <div className="text-muted small mt-1">{timeAgo(n.timestamp)}</div>
                        </div>
                      </div>
                    </a>
                  ))
                )}
              </div>
            </div>
          </li>

          {/* User dropdown */}
          <li className="nav-item dropdown">
            <a
              className="nav-icon dropdown-toggle d-inline-block d-sm-none"
              href="#"
              data-bs-toggle="dropdown"
            >
              <i className="align-middle" data-feather="settings"></i>
            </a>
            <a
              className="nav-link dropdown-toggle d-none d-sm-inline-block"
              href="#"
              data-bs-toggle="dropdown"
            >
              <img
                src={picUrl}
                className="avatar img-fluid rounded me-1"
                alt="User"
                style={{ objectFit: 'cover' }}
              />{' '}
              <span className="text-dark">{displayName}</span>
            </a>
            <div className="dropdown-menu dropdown-menu-end">
              <Link className="dropdown-item" to="/profile">
                <i className="align-middle me-1" data-feather="user"></i> Profile
              </Link>
              <Link className="dropdown-item" to="/analytics">
                <i className="align-middle me-1" data-feather="pie-chart"></i> Analytics
              </Link>
              <Link className="dropdown-item" to="/scan-history">
                <i className="align-middle me-1" data-feather="archive"></i> Scan History
              </Link>
              <div className="dropdown-divider"></div>
              <Link className="dropdown-item" to="/settings">
                <i className="align-middle me-1" data-feather="settings"></i> Settings &amp; Privacy
              </Link>
              <Link className="dropdown-item" to="/help">
                <i className="align-middle me-1" data-feather="help-circle"></i> Help Center
              </Link>
              <div className="dropdown-divider"></div>
              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                <i className="align-middle me-1" data-feather="log-out"></i> Log out
              </a>
            </div>
          </li>
        </ul>
      </div>
    </nav>
  );
}
